// /app/content/$brandId — per-brand content home.
//
// Hero + two columns: recent generations (prompt-focused list) and
// recent posts (visual cards with image_url thumbnails). The full
// media gallery (every image variant ever produced for this brand)
// lives at its own URL: /app/brands/{id}/gallery — that page also
// gets the BrandContextBar nav so the user can switch between
// gallery / calendar / comments without leaving the brand context.
// We expose a "View full gallery" CTA in the header for discoverability.
//
// Loads brand + generations + posts in parallel; partial errors don't
// block the rest of the page.

import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { StatusPill, generationStatusTone, postStatusTone } from "../../../components/StatusPill";
import { useI18n } from "../../../lib/i18n";
import {
  assetURL,
  brands as brandsAPI,
  generations,
  posts,
  type Brand,
  type Generation,
  type Post,
} from "../../../lib/api";

function BrandContent() {
  const { t } = useI18n();
  const { brandId } = Route.useParams();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [gens, setGens] = useState<Generation[] | null>(null);
  const [ps, setPs] = useState<Post[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    brandsAPI.get(brandId)
      .then((b) => !cancelled && setBrand(b))
      .catch((e) => !cancelled && setLoadErr(String(e)));
    generations.listForBrand(brandId)
      .then((r) => !cancelled && setGens(r.generations))
      .catch(() => !cancelled && setGens([]));
    posts.listForBrand(brandId)
      .then((r) => !cancelled && setPs(r.posts))
      .catch(() => !cancelled && setPs([]));
    return () => { cancelled = true; };
  }, [brandId]);

  if (loadErr) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
        <Card><p className="text-error">{loadErr}</p></Card>
      </div>
    );
  }
  if (!brand) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
        <Card><p className="text-secondary">{t("common.loading")}</p></Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12 flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-2 min-w-0">
          <span className="eyebrow">{t("content.eyebrow")}</span>
          <h1 className="font-display text-3xl font-semibold truncate" title={brand.trade_name}>
            {brand.trade_name}
          </h1>
          <p className="text-xs text-muted font-mono">/{brand.slug}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/app/brands/$brandId/gallery" params={{ brandId: brand.id }}>
            <Button variant="ghost">🖼️ {t("app.nav.gallery")}</Button>
          </Link>
          <Link to="/app/brands/$brandId/calendar" params={{ brandId: brand.id }}>
            <Button variant="ghost">🗓️ {t("calendar.cta")}</Button>
          </Link>
          <Link to="/app/content/bulk" search={{ brand_id: brand.id }}>
            <Button variant="secondary">📅 {t("content.bulk.cta")}</Button>
          </Link>
          <Link to="/app/content/new" search={{ brand_id: brand.id }}>
            <Button variant="primary">+ {t("content.new")}</Button>
          </Link>
        </div>
      </div>

      {/* Two columns: generations list + posts gallery */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Generations — text-focused list. */}
        <Card className="!p-5">
          <h2 className="font-display text-lg font-semibold mb-3">{t("content.recentGens")}</h2>
          {gens == null ? (
            <p className="text-secondary text-sm">{t("common.loading")}</p>
          ) : gens.length === 0 ? (
            <p className="text-secondary text-sm">{t("content.empty.subtitle")}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {gens.slice(0, 20).map((g) => (
                <li key={g.id}>
                  <Link
                    to="/app/generations/$generationId"
                    params={{ generationId: g.id }}
                    className="ring-nexus rounded-[10px] block hover:bg-overlay/40 transition p-3 border border-border-subtle"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <StatusPill tone={generationStatusTone(g.status)}>
                        {t(`content.gen.status.${g.status}`)}
                      </StatusPill>
                      <span className="text-[11px] font-mono text-muted">
                        {g.target_platforms.join(" · ")}
                      </span>
                    </div>
                    <p className="text-sm text-primary truncate" title={g.prompt}>{g.prompt}</p>
                    <p className="text-xs text-muted mt-1">{new Date(g.created_at).toLocaleString()}</p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Posts — visual cards. */}
        <Card className="!p-5">
          <h2 className="font-display text-lg font-semibold mb-3">{t("content.recentPosts")}</h2>
          {ps == null ? (
            <p className="text-secondary text-sm">{t("common.loading")}</p>
          ) : ps.length === 0 ? (
            <p className="text-secondary text-sm">{t("content.empty.subtitle")}</p>
          ) : (
            <ul className="grid grid-cols-2 gap-3">
              {ps.slice(0, 8).map((p) => (
                <li key={p.id}>
                  <Link
                    to="/app/posts/$postId"
                    params={{ postId: p.id }}
                    className="ring-nexus rounded-[10px] block hover:bg-overlay/40 transition border border-border-subtle overflow-hidden"
                  >
                    {p.image_url ? (
                      <div className="aspect-video bg-overlay/30">
                        <img src={assetURL(p.image_url)} alt="" loading="lazy" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="aspect-video bg-overlay/30 flex items-center justify-center">
                        <span className="text-3xl opacity-40">{platformGlyph(p.platform)}</span>
                      </div>
                    )}
                    <div className="p-2.5">
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <StatusPill tone={postStatusTone(p.status)}>
                          {t(`content.post.status.${p.status}`)}
                        </StatusPill>
                        <span className="text-[10px] font-mono text-muted">{p.platform}</span>
                      </div>
                      <p className="text-xs text-primary line-clamp-2" title={p.text_body}>
                        {p.text_body}
                      </p>
                      {p.scheduled_for ? (
                        <p className="text-[10px] text-info font-mono mt-1">
                          → {new Date(p.scheduled_for).toLocaleString()}
                        </p>
                      ) : null}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

// Per-platform fallback glyph for post cards with no image.
function platformGlyph(p: string): string {
  switch (p) {
    case "instagram": return "📸";
    case "facebook":  return "👍";
    case "linkedin":  return "💼";
    case "x":         return "🐦";
    case "tiktok":    return "🎵";
    case "telegram":  return "✈️";
    default:          return "·";
  }
}

export const Route = createFileRoute("/app/content/$brandId")({
  component: BrandContent,
});
